/**
 * Tenant Insights Service
 * Calculates payment behavior, communication preferences, and risk scores
 */

import { masterSupabase as supabase } from '../lib/supabase-master.js';

/**
 * Get comprehensive tenant insights
 */
export async function getTenantInsights(tenantId, paymentId = null) {
  try {
    const [paymentHistory, commHistory] = await Promise.all([
      getPaymentHistory(tenantId),
      getCommunicationHistory(tenantId)
    ]);

    const insights = {
      paymentBehavior: analyzePaymentBehavior(paymentHistory),
      communicationPreference: analyzeCommunicationPreference(commHistory),
      riskScore: calculateRiskScore(paymentHistory, commHistory),
      timeline: buildTimeline(paymentHistory, commHistory),
      recommendation: generateRecommendation(paymentHistory, commHistory)
    };

    return insights;
  } catch (error) {
    console.error('Get tenant insights error:', error);
    return null;
  }
}

/**
 * Get payment history for tenant
 */
async function getPaymentHistory(tenantId) {
  const { data, error } = await supabase
    .from('rent_payments')
    .select('*')
    .eq('tenant_id', tenantId)
    .order('payment_year', { ascending: false })
    .order('payment_month', { ascending: false })
    .limit(12);

  if (error) throw error;
  return data || [];
}

/**
 * Get communication history for tenant
 */
async function getCommunicationHistory(tenantId) {
  const { data, error } = await supabase
    .from('communication_events')
    .select('*')
    .eq('tenant_id', tenantId)
    .order('created_at', { ascending: false })
    .limit(20);

  if (error) throw error;
  return data || [];
}

/**
 * Analyze payment behavior
 */
function analyzePaymentBehavior(payments) {
  if (!payments || payments.length === 0) {
    return {
      score: 'unknown',
      onTimeCount: 0,
      lateCount: 0,
      avgDelayDays: 0,
      trend: 'neutral'
    };
  }

  let onTimeCount = 0;
  let lateCount = 0;
  let totalDelayDays = 0;
  let delayedPayments = 0;

  payments.forEach(payment => {
    if (payment.status === 'paid') {
      const dueDate = new Date(payment.due_date);
      const paidDate = payment.paid_date ? new Date(payment.paid_date) : new Date();
      const delayDays = Math.floor((paidDate - dueDate) / (1000 * 60 * 60 * 24));

      if (delayDays <= 0) {
        onTimeCount++;
      } else {
        lateCount++;
        totalDelayDays += delayDays;
        delayedPayments++;
      }
    } else if (payment.status === 'overdue') {
      lateCount++;
    }
  });

  const avgDelayDays = delayedPayments > 0 ? Math.round(totalDelayDays / delayedPayments) : 0;
  const totalCount = onTimeCount + lateCount;
  const onTimeRate = totalCount > 0 ? onTimeCount / totalCount : 0;

  let score = 'red';
  if (onTimeRate >= 0.9) score = 'green';
  else if (onTimeRate >= 0.7) score = 'yellow';

  // Calculate trend (last 3 vs previous 3)
  const recent = payments.slice(0, 3);
  const previous = payments.slice(3, 6);
  const recentOnTime = recent.filter(p => p.status === 'paid').length;
  const previousOnTime = previous.filter(p => p.status === 'paid').length;
  let trend = 'neutral';
  if (recentOnTime > previousOnTime) trend = 'improving';
  else if (recentOnTime < previousOnTime) trend = 'declining';

  return {
    score,
    onTimeCount,
    lateCount,
    avgDelayDays,
    onTimeRate: Math.round(onTimeRate * 100),
    trend
  };
}

/**
 * Analyze communication preference
 */
function analyzeCommunicationPreference(communications) {
  if (!communications || communications.length === 0) {
    return {
      preferredChannel: 'unknown',
      responseRate: 0,
      avgResponseTime: 0
    };
  }

  const channelStats = {};
  let totalResponses = 0;
  let totalResponseTime = 0;
  let responsesWithTime = 0;

  communications.forEach(comm => {
    const channel = comm.channel;
    if (!channelStats[channel]) {
      channelStats[channel] = { count: 0, responses: 0 };
    }
    channelStats[channel].count++;

    if (comm.status === 'delivered' || comm.status === 'read') {
      channelStats[channel].responses++;
      totalResponses++;

      if (comm.read_at && comm.created_at) {
        const responseTime = new Date(comm.read_at) - new Date(comm.created_at);
        totalResponseTime += responseTime;
        responsesWithTime++;
      }
    }
  });

  // Find preferred channel (highest response rate)
  let preferredChannel = 'unknown';
  let highestResponseRate = 0;
  Object.entries(channelStats).forEach(([channel, stats]) => {
    const rate = stats.count > 0 ? stats.responses / stats.count : 0;
    if (rate > highestResponseRate) {
      highestResponseRate = rate;
      preferredChannel = channel;
    }
  });

  const responseRate = communications.length > 0 
    ? Math.round((totalResponses / communications.length) * 100)
    : 0;

  const avgResponseTime = responsesWithTime > 0
    ? Math.round(totalResponseTime / responsesWithTime / (1000 * 60 * 60)) // hours
    : 0;

  return {
    preferredChannel,
    responseRate,
    avgResponseTime,
    channelStats
  };
}

/**
 * Calculate risk score
 */
function calculateRiskScore(payments, communications) {
  let riskPoints = 0;

  // Payment behavior (0-50 points)
  const overdueCount = payments.filter(p => p.status === 'overdue').length;
  riskPoints += Math.min(overdueCount * 10, 50);

  // Outstanding amount (0-30 points)
  const totalOutstanding = payments
    .filter(p => p.status !== 'paid')
    .reduce((sum, p) => sum + (p.expected_amount - p.received_amount), 0);
  if (totalOutstanding > 2000) riskPoints += 30;
  else if (totalOutstanding > 1000) riskPoints += 20;
  else if (totalOutstanding > 500) riskPoints += 10;

  // Communication responsiveness (0-20 points)
  const recentComms = communications.slice(0, 5);
  const noResponse = recentComms.filter(c => c.status === 'sent').length;
  riskPoints += Math.min(noResponse * 5, 20);

  // Calculate level
  let level = 'low';
  let color = 'green';
  if (riskPoints >= 70) {
    level = 'high';
    color = 'red';
  } else if (riskPoints >= 40) {
    level = 'medium';
    color = 'yellow';
  }

  return {
    level,
    color,
    points: riskPoints,
    maxPoints: 100
  };
}

/**
 * Build timeline of recent events
 */
function buildTimeline(payments, communications) {
  const events = [];

  // Add payment events
  payments.slice(0, 5).forEach(payment => {
    events.push({
      type: 'payment',
      date: payment.paid_date || payment.due_date,
      status: payment.status,
      amount: payment.expected_amount,
      description: `${payment.payment_month}/${payment.payment_year}`
    });
  });

  // Add communication events
  communications.slice(0, 5).forEach(comm => {
    events.push({
      type: 'communication',
      date: comm.created_at,
      channel: comm.channel,
      status: comm.status,
      dunningLevel: comm.dunning_level,
      description: comm.subject || comm.message?.substring(0, 50)
    });
  });

  // Sort by date descending
  events.sort((a, b) => new Date(b.date) - new Date(a.date));

  return events.slice(0, 10);
}

/**
 * Generate recommendation
 */
function generateRecommendation(payments, communications) {
  const paymentBehavior = analyzePaymentBehavior(payments);
  const commPreference = analyzeCommunicationPreference(communications);
  const risk = calculateRiskScore(payments, communications);

  let channel = commPreference.preferredChannel;
  if (channel === 'unknown') {
    channel = 'whatsapp'; // default
  }

  let tone = 'friendly';
  if (risk.level === 'high') {
    tone = 'formal';
  } else if (risk.level === 'medium') {
    tone = 'professional';
  }

  let timing = 'immediate';
  if (paymentBehavior.score === 'green') {
    timing = '2 days before due';
  }

  let dunningLevel = 1;
  const recentOverdue = payments.filter(p => p.status === 'overdue').length;
  if (recentOverdue >= 2) dunningLevel = 3;
  else if (recentOverdue >= 1) dunningLevel = 2;

  return {
    channel,
    tone,
    timing,
    dunningLevel,
    confidence: commPreference.responseRate > 50 ? 'high' : 'medium',
    reason: generateReason(paymentBehavior, commPreference, risk)
  };
}

/**
 * Generate reason for recommendation
 */
function generateReason(paymentBehavior, commPreference, risk) {
  const reasons = [];

  if (commPreference.preferredChannel !== 'unknown') {
    reasons.push(`Bevorzugt ${commPreference.preferredChannel} (${commPreference.responseRate}% Response-Rate)`);
  }

  if (paymentBehavior.avgDelayDays > 0) {
    reasons.push(`Ø ${paymentBehavior.avgDelayDays} Tage Verzug`);
  }

  if (risk.level === 'high') {
    reasons.push('Hohes Ausfallrisiko');
  }

  if (paymentBehavior.trend === 'improving') {
    reasons.push('Zahlungsverhalten verbessert sich');
  } else if (paymentBehavior.trend === 'declining') {
    reasons.push('Zahlungsverhalten verschlechtert sich');
  }

  return reasons.join(' • ');
}

export default {
  getTenantInsights
};
